import { useCallback, useEffect, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useTranslation } from "react-i18next";
import useSessionState from "../../../hooks/useSessionState";

import apiClient from "../../../helpers/apiClient";
import { useCategoriesContext } from "../../../contexts/CategoriesContext";
import { useToast } from "../../../contexts/ToastContext";
import useOpenModalParam from "../../../hooks/useOpenModalParam";
import CustomPagination from "../../../components/custom-component/CustomPagination";
import { MobileFilterBar, useTableFilters } from "../../../components/data-table";
import CategoryFormModal from "./category/CategoryFormModal";
import CategoryDetails from "./category/CategoryDetails";
import deleteFile from "../../../utilities/deleteFile";
import CategoryList from "./CategoryList";

const CATEGORY_VIEWS = {
    LIST: "list",
    DETAILS: "details",
};

function CategoryListPanel() {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const { categories, categoriesLoading, fetchCategories, totalCategories } = useCategoriesContext();

    const [view, setView] = useState(CATEGORY_VIEWS.LIST);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [currentPage, setCurrentPage] = useSessionState("categories-panel:page", 1);
    const [pageSize, setPageSize] = useSessionState("categories-panel:pageSize", 10);
    const [totalItems, setTotalItems] = useState(0);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const table = useTableFilters({ persistKey: "categories", onChange: () => setCurrentPage(1) });
    const searchParams = table.filters;

    const loadCategories = useCallback(async () => {
        const { total } = await fetchCategories(searchParams, { page: currentPage, limit: pageSize });
        setTotalItems(total ?? totalCategories);
    }, [currentPage, fetchCategories, pageSize, searchParams, totalCategories]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const openCreateModal = () => {
        setEditingCategoryId(null);
        setCategoryModalOpen(true);
    };

    const openEditModal = (categoryId) => {
        setEditingCategoryId(categoryId);
        setCategoryModalOpen(true);
    };

    const closeCategoryModal = () => {
        setCategoryModalOpen(false);
        setEditingCategoryId(null);
    };

    useOpenModalParam("addCategory", openCreateModal);

    const handleRefresh = async () => {
        await loadCategories();
    };

    const categoryActionItems = useMemo(
        () => [
            { label: t("rates.viewDetails"), key: "viewDetails" },
            { label: t("edit"), key: "edit" },
            { label: t("rates.setAsDeactive"), key: "toggleStatus" },
            { label: t("delete"), key: "delete", danger: true },
        ],
        [t],
    );

    const getActionItemsForCategory = useCallback(
        (category) =>
            categoryActionItems.map((item) => {
                if (item.key !== "toggleStatus") {
                    return item;
                }
                return {
                    ...item,
                    label:
                        category?.status === "active" ? t("rates.setAsDeactive") : t("rates.setAsActive"),
                };
            }),
        [categoryActionItems, t],
    );

    const handleStatusChange = async (category) => {
        try {
            const newStatus = category.status === "active" ? "inactive" : "active";
            const response = await apiClient.patch(`/api/car-category-v2/${category.categoryId}`, {
                status: newStatus,
            });

            if (response?.data?.success) {
                toast.success({
                    message: t("rates.statusUpdated"),
                    description: t(`rates.categoryIsNow${newStatus === "active" ? "Active" : "Inactive"}`),
                });
                await handleRefresh();
            }
        } catch (error) {
            toast.error({
                message: t("rates.failedToUpdateStatus"),
                description: error.response?.data?.message || error.message,
            });
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleDeleteCategory = (category) => {
        Modal.confirm({
            title: t("rates.areYouSure"),
            content: t("rates.cantRevert"),
            okText: t("yes"),
            cancelText: t("no"),
            centered: true,
            okButtonProps: { danger: true },
            async onOk() {
                setActionLoadingId(category.categoryId);
                try {
                    const response = await apiClient.delete(`/api/car-category-v2/${category.categoryId}`);

                    if (response?.data?.success) {
                        toast.success({
                            message: t("rates.deleted"),
                            description: t("rates.categoryDeleted"),
                        });

                        if (category.image) {
                            await deleteFile(category.image);
                        }

                        await handleRefresh();
                    }
                } catch (error) {
                    toast.error({
                        message: t("rates.error"),
                        description:
                            error.response?.data?.message || error.message || t("rates.failedToDeleteCategory"),
                    });
                } finally {
                    setActionLoadingId(null);
                }
            },
        });
    };

    const handleActionClick = (key, category) => {
        if (key === "viewDetails") {
            setSelectedCategoryId(category.categoryId);
            setView(CATEGORY_VIEWS.DETAILS);
            return;
        }
        if (key === "edit") {
            openEditModal(category.categoryId);
            return;
        }
        if (key === "toggleStatus") {
            setActionLoadingId(category.categoryId);
            handleStatusChange(category);
            return;
        }
        if (key === "delete") {
            handleDeleteCategory(category);
        }
    };

    const categoryModal = (
        <CategoryFormModal
            open={categoryModalOpen}
            categoryId={editingCategoryId}
            onCancel={closeCategoryModal}
            onSuccess={() => {
                closeCategoryModal();
                handleRefresh();
            }}
        />
    );

    if (view === CATEGORY_VIEWS.DETAILS) {
        return (
            <div className="flex min-w-0 flex-col gap-4">
                <CategoryDetails
                    embedded
                    mobileLayout
                    categoryId={selectedCategoryId}
                    onCancel={() => {
                        setView(CATEGORY_VIEWS.LIST);
                        setSelectedCategoryId(null);
                    }}
                    onEdit={() => openEditModal(selectedCategoryId)}
                    onDeleted={() => {
                        setView(CATEGORY_VIEWS.LIST);
                        setSelectedCategoryId(null);
                        handleRefresh();
                    }}
                />
                {categoryModal}
            </div>
        );
    }

    return (
        <div className="flex min-w-0 flex-col gap-4">
            <h1 className="text-lg font-bold text-[#2F2F46]">
                {t("category")}
                <span className="text-[#5D5DF6]"> ({totalItems})</span>
            </h1>

            <MobileFilterBar
                filters={table}
                search={{ key: "categoryName", placeholder: t("mobileList.searchCategoriesPlaceholder") }}
                filterLabels={{
                    categoryName: t("rates.categoryName"),
                    status: t("status"),
                    hasActivePricing: t("rate"),
                }}
            />

            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
                className="!h-11 w-full !rounded-xl !font-medium">
                {t("createCategory")}
            </Button>

            <CategoryList
                categories={categories}
                loading={categoriesLoading}
                t={t}
                getActionItems={getActionItemsForCategory}
                onActionClick={handleActionClick}
                actionLoadingId={actionLoadingId}
            />

            {totalItems > 0 ? (
                <div className="flex min-w-0 w-full justify-end">
                    <CustomPagination
                        variant="spaced"
                        totalItem={totalItems}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSizeOptions={[10, 20, 50, 100]}
                        showSizeChanger
                    />
                </div>
            ) : null}

            {categoryModal}
        </div>
    );
}

export default CategoryListPanel;
